/**
 * デッキのレシピの編集ページ（/decks/:deckId/edit[?version=]。Issue #953 / #954）。
 * 土台は左のカードの検索と絞り込み（`SearchCards`。種類（儀式・融合・シンクロ）・魔法 / 罠の細分（速攻・永続・カウンター…）・
 * 属性・種族・レベル・ATK / DEF・効果文・禁止 / 制限 / 準制限・画像があるものだけ・デッキから抜いたカード。Issue #957）。
 * その上に補助の入口: YDK から追加（`ReadYdk`）・写真から追加（準備中）。右は下書き（自動整列なら足す・抜くたびに並べ直し、保存でデッキの並びにも書く）で、保存するまで何も残らない。
 * レシピはいつも 1 行 10 枚のまま、全部がスクロールなしで画面に収まる大きさ（components/deck/fitGrid.js）。
 * 「自動整列」のチェックは左の絞り込みの下。ページ全体はレシピの右端までの幅で真ん中に寄せる（見出しのボタンの右端＝レシピの右端）。
 * 保存は「このバージョンに上書き保存」か「新しいバージョンとして保存」。上書きでそのバージョンを使う試合の記録が壊れるなら
 * サーバーが断る（`new_version_suggested`）→「新しいバージョンで保存しますか？」。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '../appInfo';
import { api } from '../api/api';
import useCoarsePointer from '../components/useCoarsePointer';
import { CardThumb, useLflist } from '../components/deck/deckParts';
import { useFitGrid } from '../components/deck/fitGrid';
import { ATTRIBUTES, KINDS, LIMITS, RACES, SUBKINDS, buildSearchParam, cardLine, hasSearchCondition, isExtraDeckCard, sortDeckCards } from '../utils/cardFacts';

export const MAX_COPIES = 3;
const EMPTY_FILTER = { query: '', language_code: 'jp', kind: '', subkind: '', attribute: 0, race: 0, level_min: '', level_max: '',
  atk_min: '', atk_max: '', def_min: '', def_max: '', text: '', limit: '', hasImage: false, removed: false };
const SEARCH_W = 380;   // 左の検索の幅
const AUTO_SORT_KEY = 'deckEditor.autoSort';
const readAutoSort = () => { try { return localStorage.getItem(AUTO_SORT_KEY) !== '0'; } catch { return true; } };

let nextKey = 1;
const entry = (c) => ({ key: nextKey++, card_id: String(c.card_id), card_name: c.card_name, card_reading: c.card_reading,
  type: c.type, level: c.level, attribute: c.attribute, race: c.race, atk: c.atk, def: c.def,
  thumbnail_url: c.thumbnail_url, language_code: c.language_code, image_status: c.image_status,
  set_code: c.set_code ?? null, rarity: c.rarity ?? null, slot_language: c.slot_language ?? null });

/** デッキから抜いたカード（純関数）: どれかの版・編集元に入っていて、今の下書きに無いカードの番号 */
export function removedCardIds(everIds, baseIds, draft) {
  const now = new Set(draft.map((d) => String(d.card_id)));
  return [...new Set([...everIds, ...baseIds].map(String))].filter((id) => !now.has(id)).sort();
}

/** 下書きにカードを足せるか（同じカードは 3 枚まで） */
export function canAdd(draft, cardId) {
  return draft.filter((d) => d.card_id === String(cardId)).length < MAX_COPIES;
}

/**
 * 保存に送る 1 枚ずつ。もとからあるカードは今の型番をそのまま送る（同じカードの何枚目かで型番が決まるので、
 * 途中の 1 枚を外したとき、うしろの枚の型番が前へ詰まる）。足したカードは絵の言語だけ（型番はデッキのページで選ぶ）
 */
export function saveCards(draft) {
  return draft.map((d) => (d.fresh
    ? (d.language_code ? { card_id: d.card_id, language_code: d.language_code } : { card_id: d.card_id })
    : { card_id: d.card_id, set_code: d.set_code ?? null, rarity: d.rarity ?? null,
      ...(['jp', 'en'].includes(d.slot_language) ? { language_code: d.slot_language } : {}) }));
}

function YdkModal({ onClose, onRead }) {
  const [text, setText] = useState('');
  const [lang, setLang] = useState('jp');
  const [busy, setBusy] = useState(false);
  const file = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => setText(String(ev.target.result ?? ''));
    reader.readAsText(f);
  };
  const read = async () => {
    setBusy(true);
    try {
      onRead(await api.project('ReadYdk', { ydk_contents: text, language_code: lang }));
    } catch (err) {
      alert('読めませんでした: ' + err.message);
      setBusy(false);
    }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">YDK から追加</h2>
        <div className="form-group">
          <input type="file" accept=".ydk,text/plain" onChange={file} style={{ color: '#aaa', fontSize: '0.9rem' }} />
          <textarea className="form-input" rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder={'#main\n12345678\n...'}
            style={{ fontFamily: 'monospace', fontSize: '0.75rem', marginTop: 6 }} />
        </div>
        <div className="form-group">
          <label className="form-label">絵の言語（型番を選ぶまで）</label>
          <select className="form-select" value={lang} onChange={(e) => setLang(e.target.value)}>
            <option value="jp">日本語</option><option value="en">English</option>
          </select>
        </div>
        <p className="card-meta">読んだカードを下書きに足します（保存するまで残りません）。</p>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={read} disabled={busy || !text.trim()}>{busy ? '読み込み中...' : '下書きに足す'}</button>
        </div>
      </div>
    </div>
  );
}

export default function DeckEditor() {
  const { deckId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const coarse = useCoarsePointer();
  const lflist = useLflist();
  const [deck, setDeck] = useState(null);
  const [base, setBase] = useState(null);           // 編集元の版 { version_id, version_no, description }
  const [isLatest, setIsLatest] = useState(true);
  const [draft, setDraft] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [searching, setSearching] = useState(false);
  const [showYdk, setShowYdk] = useState(false);
  const [showFilters, setShowFilters] = useState(!coarse);
  const [showSearch, setShowSearch] = useState(!coarse);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [autoSort, setAutoSort] = useState(readAutoSort);   // 下書きを種類・レベル順に並べる（外すと足した順）
  const [everIds, setEverIds] = useState([]);   // どれかの版に入ったことのあるカード
  const [baseIds, setBaseIds] = useState([]);   // 編集元の版のカード
  // デッキから抜いたカード: どれかの版・編集元に入っていて、今の下書きに無いもの
  const removedIds = removedCardIds(everIds, baseIds, draft);
  const seq = useRef(0);
  usePageTitle(deck ? `${deck.name} を編集` : 'デッキの編集');

  useEffect(() => {
    const version = params.get('version');
    Promise.all([api.project('GetDeck', { deck_id: Number(deckId) }), api.project('GetDeckCards', { deck_id: version ? Number(version) : Number(deckId) })])
      .then(([d, view]) => {
        setDeck(d.deck);
        setIsLatest(!d.versions?.length || d.versions[0].id === view.version_id);
        setBase({ version_id: view.version_id, version_no: view.version_no, description: view.description });
        setEverIds((d.card_ids ?? []).map(String));
        const cards = (view.cards ?? []).map(entry);
        setBaseIds(cards.map((c) => c.card_id));
        setDraft(readAutoSort() ? sortDeckCards(cards) : cards);
      })
      .catch((err) => alert('デッキを読めませんでした: ' + err.message));
  }, [deckId, params]);

  const search = useCallback(async (offset = 0) => {
    if (!hasSearchCondition(filter)) { setResults([]); setTotal(0); return; }
    const my = ++seq.current;
    setSearching(true);
    try {
      const rows = await api.project('SearchCards', buildSearchParam(filter, offset, lflist, removedIds));
      if (my !== seq.current) return;
      const list = Array.isArray(rows) ? rows : [];
      setResults((prev) => (offset ? [...prev, ...list] : list));
      setTotal(Number(list[0]?.total ?? (offset ? total : 0)));
    } catch { if (my === seq.current && !offset) setResults([]); }
    finally { if (my === seq.current) setSearching(false); }
  }, [filter, total, lflist, removedIds]);
  useEffect(() => {
    const t = setTimeout(() => search(0), 300);
    return () => clearTimeout(t);
  }, [filter, lflist, filter.removed ? removedIds.join(',') : '']); // eslint-disable-line react-hooks/exhaustive-deps

  // 下書きを変える。自動整列なら変えるたびに並べ直す（同じカードどうしの順は保つ＝何枚目の型番がずれない）
  const change = (fn) => { setDraft((prev) => (autoSort ? sortDeckCards(fn(prev)) : fn(prev))); setDirty(true); };
  const add = (card) => {
    if (!canAdd(draft, card.card_id)) { alert(`同じカードは ${MAX_COPIES} 枚までです`); return; }
    change((prev) => [...prev, { ...entry({ ...card, language_code: filter.language_code }), fresh: true }]);
  };
  const remove = (key) => change((prev) => prev.filter((d) => d.key !== key));
  const addYdk = (cards) => {
    setShowYdk(false);
    change((prev) => [...prev, ...cards.map((c) => ({ ...entry(c), fresh: true }))]);
  };

  const done = () => navigate(`/decks/${deckId}`);
  // 自動整列なら、保存した版の並びもデッキに書く（足したカードが末尾に付いたままにならないように）
  const arrange = async (versionId) => {
    if (!autoSort || !versionId) return;
    try {
      const view = await api.project('GetDeckCards', { deck_id: versionId });
      await api.project('SetDeckOrder', { deck_id: Number(deckId), slot_ids: sortDeckCards(view.cards ?? []).map((c) => c.deck_card_id) });
    } catch { /* 並びが書けなくてもレシピは保存できている */ }
  };
  const save = async (mode) => {
    setSaving(true);
    const body = { deck_id: Number(deckId), cards: saveCards(draft), description: description.trim() || undefined };
    try {
      if (mode === 'overwrite') {
        const res = await api.projectResult('SaveDeck', { ...body, mode, version_id: base.version_id });
        if (res.ok) { await arrange(res.body?.version_id); done(); return; }
        if (!res.body?.new_version_suggested) { alert('保存できませんでした: ' + (res.body?.error ?? res.status)); return; }
        const games = (res.body.games ?? []).map((g) => `・${g.duel_name ?? `記録 #${g.replay_id}`}: ${g.reason}`).join('\n');
        if (!window.confirm(`リプレイが壊れる試合があります。\n${games}\n\n新しいバージョンで保存しますか？`)) return;
      }
      const saved = await api.project('SaveDeck', { ...body, mode: 'new' });
      await arrange(saved?.version_id);
      done();
    } catch (err) {
      alert('保存できませんでした: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const leave = () => { if (!dirty || window.confirm('保存していない変更を捨てますか？')) done(); };

  const toggleAutoSort = (on) => {
    setAutoSort(on);
    if (on) setDraft((prev) => sortDeckCards(prev));
    try { localStorage.setItem(AUTO_SORT_KEY, on ? '1' : '0'); } catch { /* 覚えられなくても並びは変わる */ }
  };
  const sorted = draft;   // 自動整列のときは draft がいつも並んでいる
  const spellOrTrap = ['spell', 'trap'].includes(filter.kind);
  const main = sorted.filter((c) => !isExtraDeckCard(c));
  const extra = sorted.filter((c) => isExtraDeckCard(c));
  // ページ全体（見出しのボタン・保存の行・検索・レシピ）をレシピの右端までの幅にして真ん中に寄せる
  const outerRef = useRef(null);
  const searchW = showSearch && !coarse ? SEARCH_W + 16 : 0;
  const { ref: gridRef, center, grid, width: fitW } = useFitGrid(main.length, extra.length, { outerRef, reserve: searchW });
  const frame = { margin: '0 auto', ...(fitW ? { maxWidth: fitW + searchW } : null) };
  const set = (k) => (e) => setFilter((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const num = (k, ph) => <input className="form-input" style={{ width: 64 }} inputMode="numeric" placeholder={ph} value={filter[k]} onChange={set(k)} data-testid={`filter-${k}`} />;

  const draftGrid = (list) => (
    <div className="no-callout" style={grid}>
      {list.map((c) => (
        <div key={c.key} style={{ cursor: 'pointer' }} onClick={() => remove(c.key)} title={`${c.card_name} — 押して下書きから外す`}>
          <CardThumb card={c} lflist={lflist} />
        </div>
      ))}
    </div>
  );

  if (!deck || !base) return <div className="page"><p className="loading">読み込み中...</p></div>;
  return (
    <div className="page" style={{ maxWidth: 'none' }}>
      <div ref={outerRef}>
      <div style={frame} data-testid="editor-frame">
      <div className="page-header">
        <div style={{ minWidth: 0 }}>
          <div className="card-meta"><Link to="/decks">デッキ管理</Link> ／ <a href={`/decks/${deckId}`} onClick={(e) => { e.preventDefault(); leave(); }}>{deck.name}</a></div>
          <h1 className="page-title" style={{ margin: 0 }}>レシピを編集（v{base.version_no}{isLatest ? '・最新' : ''}から）</h1>
        </div>
        <div className="actions">
          <button className="btn btn-secondary" onClick={() => setShowYdk(true)}>YDK から追加</button>
          <button className="btn btn-secondary" disabled title="準備中（カード管理の型番の読み取りを使う予定）">写真から追加（準備中）</button>
          {coarse && <button className={`btn ${showSearch ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setShowSearch((v) => !v)}>{showSearch ? '検索を閉じる' : 'カードを探す'}</button>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        {dirty && <span className="card-meta" style={{ color: '#f5d65e' }}>未保存</span>}
        <input className="form-input" style={{ width: 220 }} placeholder="バージョンの説明（任意）" value={description}
          onChange={(e) => setDescription(e.target.value)} data-testid="save-description" />
        <button className="btn btn-primary" disabled={saving || !dirty} onClick={() => save('overwrite')} data-testid="save-overwrite">
          v{base.version_no} に上書き保存
        </button>
        <button className="btn btn-success" disabled={saving || !dirty} onClick={() => save('new')} data-testid="save-new">新しいバージョンとして保存</button>
        <button className="btn btn-secondary" onClick={leave}>やめる</button>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', position: 'relative' }}>
        {showSearch && (
          <div className="card" style={{ width: coarse ? `min(${SEARCH_W}px, 92vw)` : SEARCH_W, flexShrink: 0, padding: 12, boxSizing: 'border-box',
            ...(coarse ? { position: 'absolute', left: 0, top: 0, zIndex: 2, boxShadow: '6px 0 20px rgba(0,0,0,0.6)' } : null) }}
            data-testid="search-pane">
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <input className="form-input" style={{ flex: 1 }} placeholder="カード名・読み" value={filter.query} onChange={set('query')}
                inputMode="search" enterKeyHint="search" autoFocus={!coarse} data-testid="filter-query" />
              <select className="form-select" style={{ width: 'auto' }} value={filter.language_code} onChange={set('language_code')}>
                <option value="jp">JP</option><option value="en">EN</option>
              </select>
            </div>
            <button className="btn btn-sm btn-secondary" style={{ marginBottom: 8 }} onClick={() => setShowFilters((v) => !v)}>
              {showFilters ? '絞り込みを閉じる' : '絞り込み'}
            </button>
            {showFilters && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }} data-testid="filters">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <select className="form-select" style={{ width: 'auto' }} value={filter.kind} data-testid="filter-kind"
                    onChange={(e) => setFilter((f) => ({ ...f, kind: e.target.value, subkind: '' }))}>
                    {KINDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                  </select>
                  {spellOrTrap ? (
                    <select className="form-select" style={{ width: 'auto' }} value={filter.subkind} onChange={set('subkind')} data-testid="filter-subkind">
                      {SUBKINDS[filter.kind].map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                    </select>
                  ) : (
                    <>
                      <select className="form-select" style={{ width: 'auto' }} value={filter.attribute} onChange={set('attribute')} data-testid="filter-attribute">
                        <option value={0}>属性</option>
                        {ATTRIBUTES.map(([b, label]) => <option key={b} value={b}>{label}</option>)}
                      </select>
                      <select className="form-select" style={{ width: 'auto' }} value={filter.race} onChange={set('race')} data-testid="filter-race">
                        <option value={0}>種族</option>
                        {RACES.map(([b, label]) => <option key={b} value={b}>{label}族</option>)}
                      </select>
                    </>
                  )}
                </div>
                {!spellOrTrap && (
                  <>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span className="card-meta">レベル</span>{num('level_min', '下限')}〜{num('level_max', '上限')}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span className="card-meta">ATK</span>{num('atk_min', '下限')}〜{num('atk_max', '上限')}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span className="card-meta">DEF</span>{num('def_min', '下限')}〜{num('def_max', '上限')}
                    </div>
                  </>
                )}
                <select className="form-select" style={{ width: 'auto', alignSelf: 'flex-start' }} value={filter.limit} onChange={set('limit')} data-testid="filter-limit">
                  {LIMITS.map(([k, label]) => <option key={k} value={k}>{k ? label : '禁止・制限で絞らない'}</option>)}
                </select>
                <input className="form-input" placeholder="効果文に含む" value={filter.text} onChange={set('text')} data-testid="filter-text" />
                <label className="card-meta" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={filter.hasImage} onChange={set('hasImage')} data-testid="filter-has-image" />
                  画像があるカードだけ
                </label>
                <label className="card-meta" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={filter.removed} onChange={set('removed')} data-testid="filter-removed" />
                  デッキから抜いたカード（{removedIds.length} 種類）
                </label>
                <button className="btn btn-sm btn-secondary" style={{ alignSelf: 'flex-start' }} onClick={() => setFilter((f) => ({ ...EMPTY_FILTER, language_code: f.language_code }))}>条件を消す</button>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, margin: '4px 0 8px', paddingTop: 6, borderTop: '1px solid #223' }}>
              <label className="card-meta" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={autoSort} onChange={(e) => toggleAutoSort(e.target.checked)} data-testid="auto-sort" />
                自動整列（種類・レベル順。外すと足した順）
              </label>
            </div>
            {searching && <div className="card-meta">検索中...</div>}
            {!searching && hasSearchCondition(filter) && results.length === 0 && <div className="card-meta">該当なし</div>}
            {results.length > 0 && <div className="card-meta" style={{ marginBottom: 4 }}>{total} 枚</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: '60vh', overflowY: 'auto' }} data-testid="results">
              {results.map((c) => {
                const n = draft.filter((d) => d.card_id === String(c.card_id)).length;
                return (
                  <div key={c.card_id} style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', opacity: n >= MAX_COPIES ? 0.4 : 1 }}
                    onClick={() => add(c)} title="押して下書きに足す">
                    <div style={{ width: 36, flexShrink: 0 }}><CardThumb card={c} lflist={lflist} /></div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.card_name}</div>
                      <div className="card-meta" style={{ fontSize: '0.7rem' }}>{cardLine(c)}</div>
                    </div>
                    {n > 0 && <span className="tag">×{n}</span>}
                  </div>
                );
              })}
              {results.length > 0 && results.length < total && (
                <button className="btn btn-sm btn-secondary" onClick={() => search(results.length)} disabled={searching}>もっと見る</button>
              )}
            </div>
          </div>
        )}
        <div style={{ flex: '1 1 auto', minWidth: 0 }} data-testid="draft">
          <div ref={gridRef}>
            <div style={center} data-testid="deck-grid-center">
          {main.length > 0 && <><div className="card-meta">メインデッキ（{main.length}）</div>{draftGrid(main)}</>}
          {extra.length > 0 && <><div className="card-meta">エクストラデッキ（{extra.length}）</div>{draftGrid(extra)}</>}
            </div>
          </div>
          {draft.length === 0 && <p className="loading">カードがありません。左の検索から足すか、YDK から追加してください</p>}
        </div>

      </div>

      </div>
      </div>
      {showYdk && <YdkModal onClose={() => setShowYdk(false)} onRead={addYdk} />}
    </div>
  );
}
